import styles from "./YourPlan.module.scss";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import SubscriptionItem from "./SubscriptionItem/SubscriptionItem";
import useAppSelector from "../../../../hooks/useAppSelector";
import ConfirmPopup from "../../../../components/shared/ConfirmPopup/ConfirmPopup";
import attentionIcon from "../../../../assets/images/icons/attention.svg";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { openSuccessMessage } from "../../../../store/message/messageSlice";
import AddonItem from "./AddonItem/AddonItem";
import { handleError } from "../../../../helper/errorHandler";
import { PaymixService } from "../../../../services/paymix.service";
import { useGetAllSubscriptionsQuery } from "../../../../api/queries";
import { useGetAllAddonsQuery } from "../../../../api/queries/useGetAllAddonsQuery";
import { useGetUserSubscriptionQuery } from "../../../../api/queries/useGetUserSubscriptionQuery";

const YourPlan = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { userSubscription } = useAppSelector((state) => state.userSubscriptionSlice);
  const { data: subscriptions = [], refetch } = useGetAllSubscriptionsQuery();
  const { data: addons = [] } = useGetAllAddonsQuery();
  const { refetch: refetchUserSubscription } = useGetUserSubscriptionQuery(user?.id || null);

  const [showMore, setShowMore] = useState<boolean>(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);
  const [buttonLoader, setButtonLoader] = useState(false);

  const changeShowMoreVisible = () => {
    setShowMore(!showMore);
  };

  const cancelMembership = async () => {
    if (!userSubscription) return;
    setButtonLoader(true);
    try {
      const response = await PaymixService.cancelSubscription(userSubscription.id);
      if (response.data && response.status === 200) {
        dispatch(openSuccessMessage(t("SubscriptionCanceledSuccessfully")));
        refetchUserSubscription();
        setShowConfirmPopup(false);
        refetch();
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
    setButtonLoader(false);
  };

  const clickNoOnConfirmPopup = () => {
    setShowConfirmPopup(false);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t("YourPlan")}</h2>
      <p className={styles.subtitle}>
        {t("YouCanChangeYourPlanImmediatelyOrSelectPlanThatWorksBestForYouAfterYourTrial")}
      </p>
      <div className={styles.subscriptionsWrapper}>
        {userSubscription && (
          <div className={styles.userSubscription}>
            <h3 className={styles.name}>{userSubscription.subscription.name}</h3>
            <h3 className={styles.credits}>
              {userSubscription.subscription.credits} {t("Credits")}
            </h3>
            <p className={styles.price}>€{userSubscription.subscription.price} / mo</p>
            {userSubscription.subscription.description.length > 37 && !showMore ? (
              <p className={styles.description}>
                {userSubscription.subscription.description.slice(0, 33)}
                <span onClick={changeShowMoreVisible} className={styles.showMore}>
                  {t("ShowMore")}..
                </span>
              </p>
            ) : (
              <p className={styles.description}>
                {userSubscription.subscription.description}
                {showMore && (
                  <span onClick={changeShowMoreVisible} className={styles.showMore}>
                    {t("ShowLess")}
                  </span>
                )}
              </p>
            )}
            <div className={styles.border}></div>
            <p className={styles.date}>52 credits left - Trial ends November 24</p>
          </div>
        )}
        {!!subscriptions.length &&
          subscriptions.map((el) => {
            return <SubscriptionItem key={el.id} item={el} />;
          })}
      </div>

      {userSubscription && (
        <>
          <div className={styles.border}></div>
          <div onClick={() => setShowConfirmPopup(true)} className={styles.cancelMembership}>
            {t("CancelMembership")}
          </div>
          <div className={styles.border}></div>
          {!!addons.length && (
            <div className={styles.addons}>
              <h2 className={styles.title}>{t("Addons")}</h2>
              <p className={styles.subtitle}>
                {t("YouCanBuyExtraCreditsWithoutAffectingYourCurrentSubscription")}
              </p>
              <div className={styles.addonsWrapper}>
                {addons.map((el) => {
                  return <AddonItem key={el.id} item={el} />;
                })}
              </div>
            </div>
          )}
        </>
      )}
      {showConfirmPopup && (
        <ConfirmPopup
          iconPath={attentionIcon}
          title={t("ConfirmUnsubscription")}
          description={t("AreYouSureYouWantToUnsubscribe")}
          onClickYes={cancelMembership}
          onClickNo={clickNoOnConfirmPopup}
          textForConfirmBtn={t("Unsubscribe")}
          textForRejectBtn={t("Cancel")}
          yesButtonLoader={buttonLoader}
        />
      )}
    </div>
  );
};

export default YourPlan;
